// src/main.ts
import './ui/styles.css';
import { App } from './app';
import { load, save, available } from './core/storage';
import { renderConsentBanner, makeConsent } from './ui/consent';
import { loadClarity, event as clarityEvent, tag as clarityTag } from './ui/clarity';
import { applyChallengeFromSearch } from './core/challengeBoot';
import { requiresCookieConsent } from './core/jurisdiction';
import type { GameRecord } from './types';

function todayLocalIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function emptyRecord(): GameRecord {
  return {
    version: 1,
    installedAt: '',
    lastPlayed: null,
    currentStreak: 0,
    maxStreak: 0,
    gamesPlayed: 0,
    scoreSum: 0,
    channelErrSum: { hue: 0, sat: 0, light: 0 },
    channelErrCount: 0,
  };
}

function boot(): void {
  const root = document.getElementById('app');
  if (!root) {
    console.warn('[boot]', 'no #app root');
    return;
  }

  if (!available()) {
    // Show a banner but still let them play (in-memory only)
    const note = document.createElement('div');
    note.className = 'consent-banner';
    note.innerHTML = '<span>Stats won\'t be saved (private mode or storage disabled).</span>';
    document.body.appendChild(note);
    setTimeout(() => note.remove(), 4000);
  }

  const today = todayLocalIso();
  const stored = load() ?? emptyRecord();
  const { rec, arrived } = applyChallengeFromSearch(stored, window.location.search, today);
  if (arrived) {
    save(rec);
    // Strip ?c= so a reload or a re-share of the address bar doesn't carry
    // the friend's score around. The record now holds it.
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch (err) {
      console.warn('[challenge]', 'could not strip ?c= from URL', err);
    }
  }

  // Clarity queues events fired before its script finishes loading, so calling
  // this straight after each loadClarity() is enough to record the arrival.
  const reportArrival = (): void => {
    if (!arrived) return;
    clarityEvent('challenge_arrived');
    clarityTag('challenge_score', arrived.score);
  };

  // Cookie banner is only legally required in EU/UK/EFTA. For other regions,
  // implicitly accept analytics so the user isn't bothered with a banner.
  // See src/core/jurisdiction.ts for the timezone-based detection.
  if (!rec.consent) {
    const consentRequired = requiresCookieConsent();

    if (!consentRequired) {
      // Implicit accept outside EU — store consent + load Clarity + start.
      const consent = makeConsent(true, today);
      const next = { ...rec, consent };
      save(next);
      loadClarity();
      reportArrival();
      const app = new App(root, today, next);
      app.start();
      return;
    }

    // EU/UK/EFTA: show the banner. Render the app behind it so the page
    // isn't a near-empty void during the consent decision — the user can
    // see and even start playing while making their cookie choice.
    const startApp = (analyticsAccepted: boolean): void => {
      const consent = makeConsent(analyticsAccepted, today);
      const next = { ...rec, consent };
      save(next);
      if (analyticsAccepted) {
        loadClarity();
        reportArrival();
      }
      const app = new App(root, today, next);
      app.start();
    };

    // Render the app first (no consent persisted yet). Clarity is NOT
    // loaded — that only happens once the user explicitly accepts.
    const provisionalApp = new App(root, today, rec);
    provisionalApp.start();

    // Now overlay the banner. When the user clicks Accept/Reject, we
    // re-instantiate the App with the updated record so any analytics
    // surface (currently only Clarity, all guarded by typeof) re-initialises.
    const banner = renderConsentBanner((accept) => startApp(accept));
    document.body.appendChild(banner);
    return;
  }

  if (rec.consent.analytics) {
    loadClarity();
    reportArrival();
  }
  const app = new App(root, today, rec);
  app.start();
}

document.addEventListener('DOMContentLoaded', boot);

// Top-level safety net for uncaught render errors.
window.addEventListener('error', (e) => {
  console.warn('[uncaught]', e.message, e.error);
  // If clarity is loaded, log the error event.
  if (typeof window.clarity === 'function') {
    try { window.clarity('event', 'js_error'); } catch { /* noop */ }
  }
});
